import { ref, watch, type Ref } from 'vue'

const APP_TITLE = 'superbrain'

/**
 * Sets `document.title` to a reactive value, auto-appending the app
 * suffix. Passing null/empty leaves just the bare app title so tab
 * chrome doesn't look empty while a page is loading or on the landing
 * route.
 *
 *   useDocumentTitle(computed(() => selectedDetail.value?.title ?? null))
 *   // → "Wiki Log · superbrain" when a page is selected
 *   // → "superbrain" at the landing route
 *
 * Static-title views can pass a plain string — it's wrapped in a ref
 * internally so the watch contract stays uniform:
 *
 *   useDocumentTitle('Lint review')   // → "Lint review · superbrain"
 */
export function useDocumentTitle(source: Ref<string | null | undefined> | string | null | undefined): void {
  const ref$ = isRefLike(source) ? source : ref(source)
  watch(
    ref$,
    (value) => {
      const prefix = value?.trim()
      document.title = prefix ? `${prefix} · ${APP_TITLE}` : APP_TITLE
    },
    { immediate: true },
  )
}

function isRefLike(source: unknown): source is Ref<string | null | undefined> {
  return typeof source === 'object' && source !== null && 'value' in source
}
